import React, {useContext, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import './css/style.css';
import './mosaic/charts/ChartjsConfig';
import {GlobalContext} from "./utils/context/GlobalContextProvider.jsx";
import Routing from "./Routing.jsx";
import Spinner from "./components/Spinner.jsx";
import i18next from "i18next";
import {initReactI18next} from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import translations_cs_CZ from "../translations/translations.cs-CZ.json";
import {SystemApi, UserApi} from "./api/authority/generated/index.ts";

function DatixoApplication() {
    const {
        context,
        currentProjectId,
        setLocalizationLoaded,
        setCurrentIdentity,
        addCurrentUserProject,
        setCurrentUserProjectsLoaded,
        setCurrentUserProjectsReload,
        getAuthorityConfiguration,
        setDefaultSubscriptionValues,
        setDefaultSubscriptionValuesLoaded,
        setSupportedCurrencies,
        setSupportedCurrenciesLoaded,
        setPermissions,
    } = useContext(GlobalContext);

    const authorityConfiguration = getAuthorityConfiguration();
    const navigate = useNavigate();
    const location = useLocation();

    // DataLayer - user and project information
    useEffect(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'pageView',
            project: context.currentProjectName,
            userId: context.currentIdentity?.user?.id,
            path: location.pathname,
        });
    }, [location.pathname, context.currentProjectName, context.currentIdentity]);

    // Facebook SDK
    useEffect(() => {
        window.fbAsyncInit = () => {
            window.FB.init({
                appId: import.meta.env.VITE_META_APP_ID,
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v19.0'
            });
        };

        (function (d, s, id) {
            let js, fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) return;
            js = d.createElement(s);
            js.id = id;
            js.src = "https://connect.facebook.net/en_US/sdk.js";
            fjs.parentNode.insertBefore(js, fjs);
        }(document, 'script', 'facebook-jssdk'));
    }, []);

    // Load translations
    useEffect(() => {
        i18next
            .use(initReactI18next)
            .use(LanguageDetector)
            .use(resourcesToBackend(locateTranslation))
            .init({
                compatibilityJSON: 'v3',
                ns: ['translations'],
                lngs: ['cs-CZ'],
                defaultNs: 'translations',
                defaultLng: 'cs-CZ',
                fallbackLng: 'cs-CZ',
            })
            .then(() => setLocalizationLoaded(true));
    }, []);

    // Check user identity and log out on invalid token
    useEffect(() => {
        if (
            Object.keys(authorityConfiguration.headers).length === 0 ||
            !context.currentIdentity
        ) {
            return;
        }

        new UserApi(authorityConfiguration)
            .userDetail({userId: context.currentIdentity?.user.id})
            .catch(() => setCurrentIdentity(null));
    }, [context.currentIdentity]);

    // Load user projects only once if logged in
    useEffect(() => {
        const tokenForProject = context.currentIdentity?.token ?? null;

        if (!context.currentIdentity?.user.id) {
            setCurrentUserProjectsLoaded(true, tokenForProject); // No user, mark as loaded
            return;
        }

        let userId = context.currentIdentity.user.id;

        async function fetchProjects() {
            //let allProjects = [];

            const api = new UserApi(authorityConfiguration);

            let page = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const response = await api.userProjects({userId, page});
                response.data.forEach(project => addCurrentUserProject(project, tokenForProject));
                hasNextPage = response.pagination.hasNextPage;
                page += 1;
            }

            setCurrentUserProjectsLoaded(true, tokenForProject);


        }

        // noinspection JSIgnoredPromiseFromCall
        fetchProjects();

       /* */

      //  return allProjects;
    }, [context.currentIdentity, context.currentUserProjectsReload]);

    // Handle project reload
    useEffect(() => {
        if (!context.currentUserProjectsReload) return;

        const tokenForProject = context.currentIdentity?.token ?? null;
        setCurrentUserProjectsLoaded(false, tokenForProject);
        setCurrentUserProjectsReload(false);
    }, [context.currentUserProjectsReload]);

    // Scroll to top on route change
    useEffect(() => {
        document.querySelector('html').style.scrollBehavior = 'auto';
        window.scroll({top: 0});
        document.querySelector('html').style.scrollBehavior = '';
    }, [location.pathname]);

    // Set permissions
    useEffect(() => {
        new SystemApi(getAuthorityConfiguration())
            .projectPermissions()
            .then(r => setPermissions(r.permissions))
            .catch(e => console.error(e));
    }, []);

    // Default subscription values
    useEffect(() => {
        new SystemApi(getAuthorityConfiguration())
            .defaultSubscriptionValues()
            .then(r => {
                setDefaultSubscriptionValues(r);
                setDefaultSubscriptionValuesLoaded();
            })
            .catch(e => console.error(e));
    }, []);

    // Supported currencies
    useEffect(() => {
        new SystemApi(getAuthorityConfiguration())
            .supportedCurrencies()
            .then(r => {
                setSupportedCurrencies(r);
                setSupportedCurrenciesLoaded();
            })
            .catch(e => console.error(e));
    }, []);

    // Redirect logic for onboarding if user is logged in
    useEffect(() => {
        if (!context.currentIdentity || location.pathname.startsWith('/onboarding')) return;

        if (context.currentIdentity.user.newPasswordRequired) {
            navigate('/onboarding/password');
        } else if (
            context.currentIdentity.user.firstName === '' ||
            context.currentIdentity.user.lastName === ''
        ) {
            navigate('/onboarding/info');
        } else if (
            context.currentUserProjectsLoaded &&
            context.currentUserProjects.length === 0 &&
            context.currentUserProjectsReload === false
        ) {
            navigate('/onboarding/project');
        }
    }, [context.currentIdentity, context.currentUserProjectsLoaded, location.pathname]);

    // Rendering logic
    if (!context.localizationLoaded) {
        return (
            <div className={'h-lvh flex justify-center'}>
                <Spinner className={'spinner-large my-auto'}/>
            </div>
        );
    }

    if (!context.currentIdentity) {
        // If user is not logged in, directly show the login page
        return <Routing/>; // Assumes Routing handles /sign/in
    }

    if (
        context.currentUserProjectsLoaded &&
        context.permissionsLoaded &&
        context.defaultSubscriptionValuesLoaded &&
        context.supportedCurrenciesLoaded
    ) {
        return <Routing key={currentProjectId}/>;
    }

    return (
        <div className={'h-lvh flex justify-center'}>
            <Spinner className={'spinner-large my-auto'}/>
        </div>
    );
}

function locateTranslation(language, namespace) {
    const translations = {
        'cs': {'translations': translations_cs_CZ},
        'cs-CZ': {'translations': translations_cs_CZ},
    };
    return translations[language]?.[namespace];
}

export default DatixoApplication;